import PropTypes from 'prop-types';
import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import Dot from 'components/@extended/Dot';
import IconButton from 'components/@extended/IconButton';

// third-party
import { MenuOrientation, NavActionType } from 'config';
import useConfig from 'hooks/useConfig';
import useHeaderTheme from 'hooks/useHeaderTheme';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { useLanguage } from '../../../../../utils/i18n';

// --- helpers ---
export const getParentPathForHiddenRoute = (path) => {
  const p = String(path || '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  if (p === 'history-download-video' || p === 'history-video') return 'live-streaming';
  if (p === 'dashboard' || p === 'dashboard') return 'dashboard-vehicle';
  return p;
};

function stripQueryAndSlashes(u = '') {
  if (!u) return '';
  const withoutQuery = String(u).split('?')[0];
  return withoutQuery.replace(/^\/+|\/+$/g, '');
}

export default function NavItem({ item, level, isParents = false, setSelectedID }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { t } = useLanguage();
  const translatedTitle = item.module_name
    ? t(`menu.${item.module_name}`, item.alias || item.title || '')
    : t(`menu.${item.id}`, item.title || '');
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();

  let itemTarget = '_self';
  if (item.target) itemTarget = '_blank';

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);
    if (isParents && setSelectedID) setSelectedID(item.id);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1.25rem' : '1.5rem',
        ...(menuOrientation === MenuOrientation.HORIZONTAL && isParents && { fontSize: 20, stroke: '1.5' })
      }}
    />
  ) : (
    false
  );

  // --- normalize URL and compute selected state robustly ---
  const { pathname, search } = useLocation();
  const rawUrl = item?.link ?? item?.url ?? '';
  const normalizedUrl = stripQueryAndSlashes(rawUrl);
  const currentPath = getParentPathForHiddenRoute(pathname); // map hidden sub-pages to their parent component

  const qs = new URLSearchParams(search);
  const rawNameFromQuery = (qs.get('name') || '').toLowerCase();
  const nameFromQuery = getParentPathForHiddenRoute(rawNameFromQuery);

  const itemQs = new URLSearchParams(rawUrl.split('?')[1] || '');
  const rawItemNameFromQuery = (itemQs.get('name') || item?.modules_name || '').toLowerCase();
  const itemNameFromQuery = getParentPathForHiddenRoute(rawItemNameFromQuery);

  let isSelected = false;

  if (normalizedUrl) {
    let isPathMatched = false;
    try {
      const mp = matchPath({ path: `/${normalizedUrl}`, end: true }, pathname);
      if (mp) isPathMatched = true;
    } catch (e) {
      console.log(e);
    }

    // fallback exact or prefix match
    if (!isPathMatched) {
      if (currentPath === normalizedUrl) isPathMatched = true;
      else if (normalizedUrl && currentPath.startsWith(normalizedUrl + '/')) isPathMatched = true;
      else if (normalizedUrl && normalizedUrl.startsWith(currentPath + '/')) isPathMatched = true;
    }

    if (isPathMatched) {
      if (nameFromQuery && itemNameFromQuery) {
        isSelected = nameFromQuery === itemNameFromQuery;
      } else if (nameFromQuery && !itemNameFromQuery) {
        // If current url has name but item doesn't have it, don't select
        isSelected = false;
      } else if (!nameFromQuery && itemNameFromQuery) {
        // If item has a name but current url doesn't, don't select
        isSelected = false;
      } else {
        isSelected = true;
      }
    }
  } else {
    isSelected = false;
  }

  if (!isSelected && item.activePaths) {
    const isMatch = item.activePaths.some((path) => {
      const cleanPath = path.replace(/^\/+|\/+$/g, '');
      return currentPath === cleanPath || currentPath.startsWith(cleanPath + '/');
    });
    if (isMatch) isSelected = true;
  }

  const { headerColor, isLightHeader, iconColor } = useHeaderTheme();

  const isWhite =
    headerColor?.toLowerCase() === '#ffffff' || headerColor?.toLowerCase() === 'white' || headerColor?.toLowerCase() === '#fff';
  const activeBg = isWhite ? '#485D9A' : headerColor || '#485D9A';
  const showDarkEffects = isWhite || !isLightHeader;

  const activeGradient = showDarkEffects ? 'linear-gradient(90deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0) 100%)' : 'none';
  const hoverGradient = showDarkEffects ? 'linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(255, 255, 255, 0) 100%)' : 'none';
  const activeShadow = showDarkEffects ? '0 4px 10px rgba(0, 0, 0, 0.15)' : '0 4px 10px rgba(0, 0, 0, 0.05)';
  const activeColor = isWhite ? '#ffffff' : iconColor;
  const inactiveHoverBg = isLightHeader ? 'rgba(0, 0, 0, 0.05)' : 'rgba(72, 93, 154, 0.08)';

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <Box sx={{ position: 'relative' }}>
          <ListItemButton
            component={Link}
            to={item.url || '#'}
            target={itemTarget}
            disabled={item.disabled}
            selected={isSelected}
            onClick={() => itemHandler()}
            sx={(theme) => ({
              zIndex: 1201,
              mb: 0.5,
              borderRadius: theme.shape?.borderRadius ? theme.shape.borderRadius * 4 : 12,
              pl: drawerOpen ? `${level * 28}px` : 1.5,
              py: !drawerOpen && level === 1 ? 1.25 : 1,

              // === STATE INACTIVE ===
              color: 'text.primary',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: inactiveHoverBg,
                backgroundImage: hoverGradient
              },

              // === STATE ACTIVE ===
              '&.Mui-selected': {
                backgroundColor: `${activeBg} !important`,
                backgroundImage: `${activeGradient} !important`,
                boxShadow: activeShadow,
                color: `${activeColor} !important`,
                '& .MuiListItemIcon-root': {
                  color: `${activeColor} !important`
                },
                '&:hover': {
                  backgroundColor: `${activeBg} !important`,
                  backgroundImage: `${activeGradient} !important`
                }
              },

              // === KHUSUS MINI DRAWER (Tutup) ===
              ...(!drawerOpen &&
                level === 1 && {
                  justifyContent: 'center',
                  px: 0,
                  width: 48,
                  height: 48,
                  margin: '0 auto 8px auto'
                }),

              // === KHUSUS ANAK MENU DI DALAM POPUP (Level > 1) ===
              ...(!drawerOpen &&
                level !== 1 && {
                  width: '100%',
                  justifyContent: 'flex-start',
                  px: 2
                })
            })}
          >
            {itemIcon && (
              <ListItemIcon
                sx={{
                  minWidth: 28,
                  // Jika aktif, icon jadi putih. Jika tidak, abu-abu (text.secondary)
                  color: isSelected ? activeColor : 'text.secondary',
                  ...(!drawerOpen && {
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 'auto'
                  })
                }}
              >
                {itemIcon}
              </ListItemIcon>
            )}

            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'inherit', // Akan otomatis mewarisi warna putih/gelap dari ListItemButton
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {translatedTitle}
                  </Typography>
                }
              />
            )}

            {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
              <Chip
                color={item.chip.color}
                variant={item.chip.variant}
                size={item.chip.size}
                label={item.chip.label}
                avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              />
            )}
          </ListItemButton>

          {/* === BUTTON EXTRA ACTIONS === */}
          {(drawerOpen || (!drawerOpen && level !== 1)) &&
            item?.actions &&
            item?.actions.map((action, index) => {
              const ActionIcon = action.icon;
              const callAction = action?.function;
              return (
                <IconButton
                  key={index}
                  {...(action.type === NavActionType.FUNCTION && {
                    onClick: (event) => {
                      event.stopPropagation();
                      callAction();
                    }
                  })}
                  {...(action.type === NavActionType.LINK && {
                    component: Link,
                    to: action.url,
                    target: action.target ? '_blank' : '_self'
                  })}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 20,
                    zIndex: 1202,
                    width: 20,
                    height: 20,
                    color: isSelected ? activeColor : 'text.secondary',
                    '&:hover': {
                      backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : inactiveHoverBg
                    }
                  }}
                >
                  <ActionIcon style={{ fontSize: '1rem' }} />
                </IconButton>
              );
            })}
        </Box>
      ) : (
        // === MODE HORIZONTAL (Atas) ===
        <ListItemButton
          component={Link}
          to={item.url || '#'}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          onClick={() => itemHandler()}
          sx={(theme) => ({
            zIndex: 1201,
            borderRadius: theme.shape?.borderRadius ? theme.shape.borderRadius * 4 : 12,
            ...(isParents && { p: 1, mr: 1 }),

            color: 'text.primary',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: inactiveHoverBg,
              backgroundImage: hoverGradient
            },
            '&.Mui-selected': {
              backgroundColor: `${activeBg} !important`,
              backgroundImage: `${activeGradient} !important`,
              boxShadow: activeShadow,
              color: `${activeColor} !important`,
              '&:hover': {
                backgroundColor: `${activeBg} !important`,
                backgroundImage: `${activeGradient} !important`
              }
            }
          })}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 28,
                color: isSelected ? activeColor : 'text.secondary'
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}

          {!itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 28,
                color: isSelected ? activeColor : 'text.secondary',
                alignItems: 'center',
                justifyContent: 'flex-start'
              }}
            >
              <Dot size={4} color="inherit" />
            </ListItemIcon>
          )}

          <ListItemText
            primary={
              <Typography
                variant="h6"
                sx={{
                  color: 'inherit',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  fontWeight: isSelected ? 600 : 400
                }}
              >
                {translatedTitle}
              </Typography>
            }
          />

          {item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
      )}
    </>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.func, PropTypes.any])
};
